import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useClientPagination } from '@/hooks/useClientPagination';
import type {
  CarryEntityFund,
  CarryEntityHolder,
  CarryEntityHolding,
} from '@/types/carryFund';
import { formatUnits } from '../funds/carryFundValues';

interface CarryEntityHoldersTableProps {
  funds: CarryEntityFund[];
  holders: CarryEntityHolder[];
}

function formatCount(units: number): string {
  return units ? formatUnits(units) : '–';
}

function unitsIn(holdings: CarryEntityHolding[], fundId: string): number {
  return holdings.find((h) => h.fund_id === fundId)?.units ?? 0;
}

export function CarryEntityHoldersTable({
  funds,
  holders,
}: CarryEntityHoldersTableProps) {
  const { pageItems: shown, paginationProps } = useClientPagination(
    holders,
    10
  );

  return (
    <div className="space-y-4">
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              {funds.map((fund) => (
                <TableHead key={fund.fund_id} className="text-right">
                  {fund.name}
                </TableHead>
              ))}
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((holder) => (
              <TableRow key={holder.fund_member_id}>
                <TableCell>
                  <div className="font-medium">{holder.full_name}</div>
                  {holder.vesting_frozen && (
                    <div className="text-xs text-muted-foreground">
                      vesting partly frozen
                    </div>
                  )}
                </TableCell>
                {funds.map((fund) => (
                  <TableCell key={fund.fund_id} className="text-right">
                    {formatCount(unitsIn(holder.holdings, fund.fund_id))}
                  </TableCell>
                ))}
                <TableCell className="text-right font-semibold">
                  {formatUnits(holder.total_units)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Pagination
        {...paginationProps}
        showSizeChanger
        pageSizeOptions={[10, 25, 50]}
      />
    </div>
  );
}
